import React, { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  Power,
  Search,
  ChevronLeft,
  ChevronRight,
  Ticket,
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminApi from '../../apis/adminApis';

interface PromoCode {
  id: number;
  code: string;
  type: string;
  max_uses: number | null;
  times_used: number;
  expires_at: string | null;
  is_active: boolean;
  description: string | null;
  created_at: string;
}

type Mode = 'random' | 'custom';

const AccessCodes: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<PromoCode[]>([]);

  // Form state
  const [mode, setMode] = useState<Mode>('random');
  const [customCode, setCustomCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [maxUses, setMaxUses] = useState<string>(''); // blank = unlimited
  const [expiresAt, setExpiresAt] = useState('');
  const [description, setDescription] = useState('');

  const fetchCodes = async (targetPage = page) => {
    setLoading(true);
    try {
      const res: any = await AdminApi.getPromoCodes({ page: targetPage, search });
      const paginator = res?.data?.data;
      setCodes(paginator?.data || []);
      setLastPage(paginator?.last_page || 1);
      setPage(paginator?.current_page || 1);
    } catch (err) {
      toast.error('Failed to load access codes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setCustomCode('');
    setQuantity(1);
    setMaxUses('');
    setExpiresAt('');
    setDescription('');
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'custom' && !customCode.trim()) {
      toast.error('Enter a custom code or switch to Random.');
      return;
    }

    setCreating(true);
    try {
      const payload: any = {
        mode,
        max_uses: maxUses.trim() === '' ? null : Number(maxUses),
        expires_at: expiresAt || null,
        description: description.trim() || null,
      };
      if (mode === 'custom') {
        payload.code = customCode.trim();
      } else {
        payload.quantity = quantity;
      }

      const res: any = await AdminApi.createPromoCode(payload);
      const created: PromoCode[] = res?.data?.data || [];
      setJustCreated(created);
      toast.success(res?.data?.message || 'Access code(s) created.');
      resetForm();
      fetchCodes(1);
    } catch (err: any) {
      const apiErrors = err?.response?.data?.errors;
      const msg =
        apiErrors?.code?.[0] ||
        err?.response?.data?.message ||
        'Failed to create access code.';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (code: PromoCode) => {
    try {
      await AdminApi.togglePromoCode(code.id);
      toast.success(code.is_active ? 'Code deactivated.' : 'Code activated.');
      fetchCodes();
    } catch {
      toast.error('Failed to update code.');
    }
  };

  const handleDelete = async (code: PromoCode) => {
    if (!window.confirm(`Delete code "${code.code}"? This cannot be undone.`)) return;
    try {
      await AdminApi.deletePromoCode(code.id);
      toast.success('Code deleted.');
      fetchCodes();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete code.');
    }
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedCode(value);
      setTimeout(() => setCopiedCode(null), 1500);
    });
  };

  const copyAllCreated = () => {
    if (!justCreated.length) return;
    copyToClipboard(justCreated.map((c) => c.code).join('\n'));
    toast.success('All new codes copied.');
  };

  const usageLabel = (c: PromoCode) =>
    c.max_uses === null ? `${c.times_used} / ∞` : `${c.times_used} / ${c.max_uses}`;

  return (
    <div className="max-w-6xl mx-auto">
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
          <Ticket className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Access Codes</h1>
          <p className="text-sm text-gray-500">
            Generate codes new users redeem at signup for free 1 TB lifetime access.
          </p>
        </div>
      </div>

      {/* Generator */}
      <form onSubmit={handleGenerate} className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex gap-2 mb-5">
          <button
            type="button"
            onClick={() => setMode('random')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              mode === 'random' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Random
          </button>
          <button
            type="button"
            onClick={() => setMode('custom')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              mode === 'custom' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            Custom
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mode === 'custom' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom code</label>
              <input
                type="text"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                placeholder="e.g. WELCOME2026"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <p className="text-xs text-gray-400 mt-1">
                Letters, numbers, hyphens, underscores. Must not already exist.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                How many codes
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max uses per code
            </label>
            <input
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Blank = unlimited"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires (optional)
            </label>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. July launch batch"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
        </div>

        <div className="mt-5">
          <button
            type="submit"
            disabled={creating}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {creating ? 'Generating…' : 'Generate code(s)'}
          </button>
        </div>
      </form>

      {/* Just-created codes */}
      {justCreated.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-green-800">
              {justCreated.length} new code(s) — copy and share
            </h3>
            <button
              onClick={copyAllCreated}
              className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline"
            >
              <Copy className="w-3.5 h-3.5" /> Copy all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {justCreated.map((c) => (
              <button
                key={c.id}
                onClick={() => copyToClipboard(c.code)}
                className="inline-flex items-center gap-2 bg-white border border-green-300 rounded-lg px-3 py-1.5 font-mono text-sm text-gray-800 hover:bg-green-100"
              >
                {c.code}
                {copiedCode === c.code ? (
                  <Check className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-gray-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchCodes(1);
            }}
            className="relative"
          >
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code…"
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </form>
          <button
            onClick={() => fetchCodes()}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Uses</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Note</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                    No access codes yet. Generate some above.
                  </td>
                </tr>
              ) : (
                codes.map((c) => {
                  const exhausted =
                    c.max_uses !== null && c.times_used >= c.max_uses;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => copyToClipboard(c.code)}
                          className="inline-flex items-center gap-2 font-mono font-medium text-gray-800"
                        >
                          {c.code}
                          {copiedCode === c.code ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-gray-300" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{usageLabel(c)}</td>
                      <td className="px-4 py-3">
                        {!c.is_active ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                            Inactive
                          </span>
                        ) : exhausted ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700">
                            Exhausted
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.expires_at
                          ? new Date(c.expires_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{c.description || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleToggle(c)}
                            title={c.is_active ? 'Deactivate' : 'Activate'}
                            className={`p-1.5 rounded-lg ${
                              c.is_active
                                ? 'text-yellow-600 hover:bg-yellow-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                          >
                            <Power className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            title="Delete"
                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="flex items-center justify-end gap-2 p-4 border-t">
            <button
              disabled={page <= 1}
              onClick={() => fetchCodes(page - 1)}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {lastPage}
            </span>
            <button
              disabled={page >= lastPage}
              onClick={() => fetchCodes(page + 1)}
              className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessCodes;
